import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { normalizeObjectStrings } from '../lib/utils';
import SupportWidget from '../components/SupportWidget';
import { 
  Heart, ArrowLeft, Plus, Users, UserCheck, UserX, Clock,
  Upload, Trash2, Search, FileSpreadsheet, CheckCircle2, XCircle,
  MessageSquare, Baby, UserPlus
} from 'lucide-react';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;

export default function GuestManager() {
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState({ total: 0, confirmed: 0, pending: 0, declined: 0 });
  const [rsvpMode, setRsvpMode] = useState('open');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [newGuestName, setNewGuestName] = useState('');
  const [newGuestHasChildren, setNewGuestHasChildren] = useState(false);
  const [newGuestChildrenCount, setNewGuestChildrenCount] = useState('1');
  const [newGuestChildrenDetails, setNewGuestChildrenDetails] = useState([{ name: '', age: '' }]);
  const [newGuestHasCompanion, setNewGuestHasCompanion] = useState(false);
  const [newGuestCompanionCount, setNewGuestCompanionCount] = useState('1');
  const [newGuestCompanionNames, setNewGuestCompanionNames] = useState(['']);
  const [importText, setImportText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [changingMode, setChangingMode] = useState(false);
  const [messageViewer, setMessageViewer] = useState({
    open: false,
    title: '',
    author: '',
    message: '',
  });
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const clampPeopleCount = (value) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return 1;
    return Math.max(1, Math.min(20, parsed));
  };

  const resizeChildrenDetails = (count, previous = newGuestChildrenDetails) => {
    const next = Array.from({ length: count }, (_, index) => ({
      name: previous[index]?.name || '',
      age: previous[index]?.age ?? '',
    }));
    setNewGuestChildrenDetails(next);
  };

  const resizeCompanionNames = (count, previous = newGuestCompanionNames) => {
    const next = Array.from({ length: count }, (_, index) => previous[index] || '');
    setNewGuestCompanionNames(next);
  };

  const resetAddGuestForm = () => {
    setNewGuestName('');
    setNewGuestHasChildren(false);
    setNewGuestChildrenCount('1');
    setNewGuestChildrenDetails([{ name: '', age: '' }]);
    setNewGuestHasCompanion(false);
    setNewGuestCompanionCount('1');
    setNewGuestCompanionNames(['']);
  };

  const openAddGuestModal = () => {
    resetAddGuestForm();
    setShowAddModal(true);
  };

  const handleChildrenCountChange = (value) => {
    const normalizedCount = clampPeopleCount(value);
    setNewGuestChildrenCount(String(normalizedCount));
    resizeChildrenDetails(normalizedCount);
  };

  const handleCompanionCountChange = (value) => {
    const normalizedCount = clampPeopleCount(value);
    setNewGuestCompanionCount(String(normalizedCount));
    resizeCompanionNames(normalizedCount);
  };

  const handleChildDetailChange = (index, field, value) => {
    setNewGuestChildrenDetails((current) => {
      const next = [...current];
      if (!next[index]) next[index] = { name: '', age: '' };
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleCompanionNameChange = (index, value) => {
    setNewGuestCompanionNames((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  const formatGuestStatus = (confirmed) => {
    if (confirmed === true) return 'Confirmado';
    if (confirmed === false) return 'Recusado';
    return 'Pendente';
  };

  const openMessageViewer = (title, author, message) => {
    setMessageViewer({
      open: true,
      title,
      author: author || '',
      message: String(message || '').trim(),
    });
  };

  const handleMessageViewerChange = (open) => {
    if (open) {
      setMessageViewer((current) => ({ ...current, open: true }));
      return;
    }
    setMessageViewer({
      open: false,
      title: '',
      author: '',
      message: '',
    });
  };

  const formatCsvCell = (value) => {
    const normalizedValue = String(value ?? '')
      .replace(/\r?\n/g, ' ')
      .trim()
      .replace(/"/g, '""');
    return `"${normalizedValue}"`;
  };

  const formatChildrenDetailsForCsv = (guest) => {
    const details = Array.isArray(guest.children_details) ? guest.children_details : [];
    if (!details.length) return '';
    return details
      .map((child) => {
        const childName = String(child?.name || '').trim();
        const childAge = child?.age;
        if (!childName) return '';
        if (Number.isFinite(Number(childAge))) {
          return `${childName} (${childAge} anos)`;
        }
        return childName;
      })
      .filter(Boolean)
      .join(' | ');
  };

  const formatCompanionNamesForCsv = (guest) => {
    const names = Array.isArray(guest.companion_names) ? guest.companion_names : [];
    if (!names.length) return '';
    return names.map((name) => String(name || '').trim()).filter(Boolean).join(' | ');
  };

  const formatChildrenDetailsForDisplay = (guest) => (
    formatChildrenDetailsForCsv(guest).replace(/\s\|\s/g, ', ')
  );

  const formatCompanionNamesForDisplay = (guest) => (
    formatCompanionNamesForCsv(guest).replace(/\s\|\s/g, ', ')
  );

  const downloadCsvFile = (filename, headers, rows) => {
    const csvContent = [
      headers.map(formatCsvCell).join(';'),
      ...rows.map((row) => row.map(formatCsvCell).join(';')),
    ].join('\r\n');

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const fetchData = async () => {
    try {
      const [guestsRes, rsvpsRes, siteRes] = await Promise.all([
        axios.get(`${API}/guests`, { withCredentials: true }),
        axios.get(`${API}/rsvps`, { withCredentials: true }),
        axios.get(`${API}/sites/my`, { withCredentials: true })
      ]);
      setGuests(normalizeObjectStrings(guestsRes.data.guests || []));
      setStats(normalizeObjectStrings(guestsRes.data.stats || { total: 0, confirmed: 0, pending: 0, declined: 0 }));
      setRsvps(normalizeObjectStrings(rsvpsRes.data || []));
      setRsvpMode(siteRes.data?.rsvp_mode || 'open');
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = async (mode) => {
    if (changingMode || mode === rsvpMode) return;
    const previousMode = rsvpMode;
    setChangingMode(true);
    try {
      setRsvpMode(mode);
      await axios.put(`${API}/sites/rsvp-mode`, { rsvp_mode: mode }, { withCredentials: true });
      toast.success(mode === 'predefined' ? 'Modo Lista Pré-definida ativado!' : 'Modo Lista Aberta ativado!');
    } catch (error) {
      setRsvpMode(previousMode);
      await fetchData();
      toast.error(error.response?.data?.detail || 'Erro ao alterar modo de confirmação');
    } finally {
      setChangingMode(false);
    }
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    if (!newGuestName.trim()) return;

    const childrenCount = newGuestHasChildren
      ? Number.parseInt(newGuestChildrenCount, 10)
      : 0;
    const extraCompanions = newGuestHasCompanion
      ? Number.parseInt(newGuestCompanionCount, 10)
      : 0;

    if (newGuestHasChildren && (!Number.isFinite(childrenCount) || childrenCount < 1)) {
      toast.error('Informe a quantidade de filhos.');
      return;
    }
    if (newGuestHasCompanion && (!Number.isFinite(extraCompanions) || extraCompanions < 1)) {
      toast.error('Informe a quantidade de acompanhantes.');
      return;
    }

    const childrenDetails = newGuestHasChildren
      ? newGuestChildrenDetails.slice(0, childrenCount).map((child) => ({
          name: String(child.name || '').trim(),
          age: child.age === '' ? null : Number.parseInt(child.age, 10),
        }))
      : [];

    if (newGuestHasChildren) {
      const hasInvalidChild = childrenDetails.some(
        (child) => !child.name || !Number.isFinite(child.age) || child.age < 0 || child.age > 30
      );
      if (hasInvalidChild) {
        toast.error('Preencha nome e idade válida para todos os filhos.');
        return;
      }
    }

    const companionNames = newGuestHasCompanion
      ? newGuestCompanionNames.slice(0, extraCompanions).map((name) => String(name || '').trim())
      : [];

    if (newGuestHasCompanion && companionNames.some((name) => !name)) {
      toast.error('Preencha o nome de todos os acompanhantes.');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/guests`,
        {
          name: newGuestName.trim(),
          has_children: Boolean(newGuestHasChildren),
          children_count: newGuestHasChildren ? childrenCount : 0,
          children_details: newGuestHasChildren ? childrenDetails : [],
          extra_companions: newGuestHasCompanion ? extraCompanions : 0,
          companion_names: newGuestHasCompanion ? companionNames : [],
        },
        { withCredentials: true }
      );
      toast.success('Convidado adicionado!');
      resetAddGuestForm();
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar convidado');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImportGuests = async () => {
    const names = importText.split('\n').map(n => n.trim()).filter(n => n);
    if (names.length === 0) {
      toast.error('Nenhum nome para importar');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/guests/import`, { guests: names }, { withCredentials: true });
      toast.success(response.data.message);
      setImportText('');
      setShowImportModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao importar convidados');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        const lines = text.split(/[\r\n]+/).filter(line => line.trim());
        const startIndex = lines[0]?.toLowerCase().includes('nome') ? 1 : 0;
        const names = lines.slice(startIndex).map(line => {
          const parts = line.split(',');
          return parts[0].trim().replace(/"/g, '');
        }).filter(n => n);
        setImportText(names.join('\n'));
      }
    };
    reader.readAsText(file);
  };

  const handleDeleteGuest = async (guestId) => {
    
    try {
      await axios.delete(`${API}/guests/${guestId}`, { withCredentials: true });
      toast.success('Convidado removido!');
      const updatedGuests = guests.filter(guest => guest.guest_id !== guestId);
      setGuests(updatedGuests);

      setStats({
        total: updatedGuests.length,
        confirmed: updatedGuests.filter(guest => guest.confirmed === true).length,
        pending: updatedGuests.filter(guest => guest.confirmed === null).length,
        declined: updatedGuests.filter(guest => guest.confirmed === false).length
      });
    } catch (error) {
      toast.error('Erro ao remover convidado');
    }
  };

  const handleExportGuests = () => {
    const exportDate = new Date().toISOString().slice(0, 10);

    if (rsvpMode === 'predefined') {
      if (!guests.length) {
        toast.error('Não há convidados para exportar.');
        return;
      }

      const rows = guests.map((guest) => [
        guest.name || '',
        formatGuestStatus(guest.confirmed),
        guest.children_count ?? 0,
        formatChildrenDetailsForCsv(guest),
        guest.extra_companions ?? 0,
        formatCompanionNamesForCsv(guest),
        guest.confirmed_at ? new Date(guest.confirmed_at).toLocaleString('pt-BR') : '',
        guest.message || '',
      ]);

      downloadCsvFile(
        `convidados-${exportDate}.csv`,
        ['Nome', 'Status', 'Filhos (quantidade)', 'Filhos (nomes e idades)', 'Acompanhantes (quantidade)', 'Acompanhantes (nomes)', 'Data de confirmação', 'Mensagem'],
        rows
      );
      toast.success('Lista de convidados exportada com sucesso.');
      return;
    }

    if (!rsvps.length) {
      toast.error('Não há confirmações para exportar.');
      return;
    }

    const rows = rsvps.map((rsvp) => [
      rsvp.guest_name || '',
      rsvp.attending ? 'Confirmou presença' : 'Recusou',
      rsvp.guests_count ?? '',
      rsvp.created_at ? new Date(rsvp.created_at).toLocaleString('pt-BR') : '',
      rsvp.message || '',
    ]);

    downloadCsvFile(
      `confirmacoes-rsvp-${exportDate}.csv`,
      ['Nome', 'Status', 'Qtd. acompanhantes', 'Data da resposta', 'Mensagem'],
      rows
    );
    toast.success('Lista de confirmações exportada com sucesso.');
  };

  const filteredGuests = guests.filter(guest => {
    const matchesSearch = guest.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'confirmed' && guest.confirmed === true) ||
      (filterStatus === 'pending' && guest.confirmed === null) ||
      (filterStatus === 'declined' && guest.confirmed === false);
    return matchesSearch && matchesFilter;
  });

  // For open mode, use rsvps
  const confirmedRsvps = rsvps.filter(r => r.attending);
  const openModeStats = {
    total: rsvps.length,
    confirmed: confirmedRsvps.length,
    pending: 0,
    declined: rsvps.filter(r => !r.attending).length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF]">
        <div className="w-12 h-12 border-4 border-[#E8B7B1] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user?.has_subscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF] px-4">
        <div className="text-center max-w-md">
          <Users className="w-16 h-16 text-[#333333] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1C1C1C] mb-2">Assinatura Necessária</h1>
          <p className="text-[#333333] mb-6">Ative seu plano para gerenciar a lista de convidados.</p>
          <Link to="/planos">
            <Button className="bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full">
              Ver Planos
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1EF]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-[#333333]" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                  Lista de Convidados
                </h1>
                <p className="text-sm text-[#333333]">Gerencie seus convidados e confirmações</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleExportGuests}
                className="rounded-full"
                data-testid="export-guests-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Exportar lista
              </Button>
              {rsvpMode === 'predefined' && (
                <>
                <Button
                  variant="outline"
                  onClick={() => setShowImportModal(true)}
                  className="rounded-full"
                  data-testid="import-guests-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Importar
                </Button>
                <Button
                  onClick={openAddGuestModal}
                  className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full"
                  data-testid="add-guest-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* RSVP Mode Selector */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-8">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-bold text-[#1C1C1C]">Modo de Confirmação</h2>
            {changingMode && (
              <span className="inline-flex items-center gap-2 text-xs text-[#7E6B5F]">
                <span className="w-3 h-3 border-2 border-[#E8B7B1] border-t-transparent rounded-full animate-spin" />
                Salvando modo...
              </span>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => handleModeChange('predefined')}
              disabled={changingMode}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                rsvpMode === 'predefined'
                  ? 'border-[#E8B7B1] bg-[#E8B7B1]/5'
                  : 'border-slate-200 hover:border-slate-300'
              } ${changingMode ? 'opacity-70 cursor-not-allowed' : ''}`}
              data-testid="mode-predefined-btn"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  rsvpMode === 'predefined' ? 'bg-[#E8B7B1] text-white' : 'bg-slate-100 text-[#333333]'
                }`}>
                  <UserCheck className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#1C1C1C]">Lista Pré-definida</h3>
                  <p className="text-sm text-[#333333] mt-1">
                    Você cadastra os convidados. Eles buscam o nome para confirmar.
                  </p>
                </div>
                {rsvpMode === 'predefined' && (
                  <CheckCircle2 className="w-5 h-5 text-[#1C1C1C]" />
                )}
              </div>
            </button>

            <button
              onClick={() => handleModeChange('open')}
              disabled={changingMode}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                rsvpMode === 'open'
                  ? 'border-[#E8B7B1] bg-[#E8B7B1]/5'
                  : 'border-slate-200 hover:border-slate-300'
              } ${changingMode ? 'opacity-70 cursor-not-allowed' : ''}`}
              data-testid="mode-open-btn"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  rsvpMode === 'open' ? 'bg-[#E8B7B1] text-white' : 'bg-slate-100 text-[#333333]'
                }`}>
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#1C1C1C]">Lista Aberta</h3>
                  <p className="text-sm text-[#333333] mt-1">
                    Qualquer pessoa pode confirmar preenchendo o nome.
                  </p>
                </div>
                {rsvpMode === 'open' && (
                  <CheckCircle2 className="w-5 h-5 text-[#1C1C1C]" />
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1C1C1C]" data-testid="total-guests">
                  {rsvpMode === 'predefined' ? stats.total : openModeStats.total}
                </p>
                <p className="text-sm text-[#333333]">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600" data-testid="confirmed-guests">
                  {rsvpMode === 'predefined' ? stats.confirmed : openModeStats.confirmed}
                </p>
                <p className="text-sm text-[#333333]">Confirmados</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600" data-testid="pending-guests">
                  {rsvpMode === 'predefined' ? stats.pending : openModeStats.pending}
                </p>
                <p className="text-sm text-[#333333]">Pendentes</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600" data-testid="declined-guests">
                  {rsvpMode === 'predefined' ? stats.declined : openModeStats.declined}
                </p>
                <p className="text-sm text-[#333333]">Recusados</p>
              </div>
            </div>
          </div>
        </div>

        {/* Guest List - Predefined Mode */}
        {rsvpMode === 'predefined' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar convidado..."
                    className="pl-10 h-11 rounded-lg"
                    data-testid="search-guest-input"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {['all', 'confirmed', 'pending', 'declined'].map((status) => (
                    <Button
                      key={status}
                      variant={filterStatus === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilterStatus(status)}
                      className={`rounded-full ${filterStatus === status ? 'bg-[#E8B7B1]' : ''}`}
                    >
                      {status === 'all' && 'Todos'}
                      {status === 'confirmed' && 'Confirmados'}
                      {status === 'pending' && 'Pendentes'}
                      {status === 'declined' && 'Recusados'}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {filteredGuests.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-[#1C1C1C] mb-2">
                  {guests.length === 0 ? 'Nenhum convidado cadastrado' : 'Nenhum resultado encontrado'}
                </h3>
                <p className="text-[#333333] mb-4">
                  {guests.length === 0 ? 'Adicione convidados à sua lista ou importe de um arquivo.' : 'Tente buscar por outro nome.'}
                </p>
                {guests.length === 0 && (
                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={() => setShowImportModal(true)} className="rounded-full">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Importar Lista
                    </Button>
                    <Button onClick={openAddGuestModal} className="bg-[#E8B7B1] text-white rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredGuests.map((guest) => (
                  <div key={guest.guest_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        guest.confirmed === true ? 'bg-green-100' :
                        guest.confirmed === false ? 'bg-red-100' : 'bg-slate-100'
                      }`}>
                        {guest.confirmed === true ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : guest.confirmed === false ? (
                          <XCircle className="w-5 h-5 text-red-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-[#333333]" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-[#1C1C1C]">{guest.name}</p>
                        <p className="text-sm text-[#333333]">
                          {guest.confirmed === true ? (
                            <>Confirmado {guest.confirmed_at && `em ${new Date(guest.confirmed_at).toLocaleDateString('pt-BR')}`}</>
                          ) : guest.confirmed === false ? (
                            'Recusou'
                          ) : (
                            'Aguardando confirmação'
                          )}
                        </p>
                        {(Number(guest.children_count || 0) > 0 || Number(guest.extra_companions || 0) > 0) && (
                          <p className="text-xs text-[#6B7280] mt-1 flex flex-wrap gap-3">
                            {Number(guest.children_count || 0) > 0 && (
                              <span className="inline-flex items-center gap-1">
                                <Baby className="w-3.5 h-3.5" />
                                Filhos: {Number(guest.children_count || 0)}
                                {formatChildrenDetailsForDisplay(guest) && (
                                  <span className="text-[#7E6B5F]">
                                    {' '}({formatChildrenDetailsForDisplay(guest)})
                                  </span>
                                )}
                              </span>
                            )}
                            {Number(guest.extra_companions || 0) > 0 && (
                              <span className="inline-flex items-center gap-1">
                                <UserPlus className="w-3.5 h-3.5" />
                                Acompanhantes: {Number(guest.extra_companions || 0)}
                                {formatCompanionNamesForDisplay(guest) && (
                                  <span className="text-[#7E6B5F]">
                                    {' '}({formatCompanionNamesForDisplay(guest)})
                                  </span>
                                )}
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {guest.message && (
                        <button
                          type="button"
                          onClick={() => openMessageViewer('Mensagem do convidado', guest.name, guest.message)}
                          className="text-[#333333] hover:text-[#C8A97E] transition-colors"
                          aria-label={`Ver mensagem de ${guest.name || 'convidado'}`}
                        >
                          <MessageSquare className="w-5 h-5" />
                        </button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteGuest(guest.guest_id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Open Mode - Show RSVPs */}
        {rsvpMode === 'open' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-bold text-[#1C1C1C]">Confirmações Recebidas</h3>
              <p className="text-sm text-[#333333]">Pessoas que confirmaram presença no seu site</p>
            </div>
            
            {rsvps.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="w-16 h-16 text-[#C8A97E] mx-auto mb-4" />
                <h3 className="text-lg font-bold text-[#1C1C1C] mb-2">Nenhuma confirmação ainda</h3>
                <p className="text-[#333333]">
                  Quando os convidados confirmarem presença no seu site, aparecerão aqui.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {rsvps.map((rsvp) => (
                  <div key={rsvp.rsvp_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        rsvp.attending ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {rsvp.attending ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-[#1C1C1C]">{rsvp.guest_name}</p>
                        <p className="text-sm text-[#333333]">
                          {rsvp.attending ? 'Confirmou presença' : 'Não poderá comparecer'}
                          {rsvp.created_at && ` em ${new Date(rsvp.created_at).toLocaleDateString('pt-BR')}`}
                        </p>
                      </div>
                    </div>
                    {rsvp.message && (
                      <button
                        type="button"
                        onClick={() => openMessageViewer('Mensagem da confirmação', rsvp.guest_name, rsvp.message)}
                        className="text-[#333333] hover:text-[#C8A97E] transition-colors"
                        aria-label={`Ver mensagem de ${rsvp.guest_name || 'convidado'}`}
                      >
                        <MessageSquare className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add Guest Modal */}
      <Dialog
        open={showAddModal}
        onOpenChange={(open) => {
          setShowAddModal(open);
          if (!open) {
            resetAddGuestForm();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              Adicionar Convidado
            </DialogTitle>
            <DialogDescription>
              Digite o nome completo do convidado
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddGuest} className="space-y-4">
            <div>
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                Nome Completo
              </Label>
              <Input
                value={newGuestName}
                onChange={(e) => setNewGuestName(e.target.value)}
                placeholder="Ex: Maria Silva"
                className="h-12 rounded-lg"
                required
                autoFocus
                data-testid="new-guest-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider block">
                Tem filhos?
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={newGuestHasChildren ? 'outline' : 'default'}
                  className={`rounded-full ${newGuestHasChildren ? '' : 'bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]'}`}
                  onClick={() => {
                    setNewGuestHasChildren(false);
                    setNewGuestChildrenCount('1');
                    setNewGuestChildrenDetails([{ name: '', age: '' }]);
                  }}
                >
                  Não
                </Button>
                <Button
                  type="button"
                  variant={newGuestHasChildren ? 'default' : 'outline'}
                  className={`rounded-full ${newGuestHasChildren ? 'bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]' : ''}`}
                  onClick={() => {
                    setNewGuestHasChildren(true);
                    const normalizedCount = clampPeopleCount(newGuestChildrenCount || '1');
                    setNewGuestChildrenCount(String(normalizedCount));
                    resizeChildrenDetails(normalizedCount);
                  }}
                >
                  Sim
                </Button>
              </div>
              {newGuestHasChildren && (
                <div className="space-y-2">
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={newGuestChildrenCount}
                    onChange={(e) => handleChildrenCountChange(e.target.value)}
                    placeholder="Quantidade de filhos"
                    className="h-11 rounded-lg"
                  />
                  <div className="grid gap-2">
                    {newGuestChildrenDetails
                      .slice(0, clampPeopleCount(newGuestChildrenCount))
                      .map((child, index) => (
                        <div key={`child-${index}`} className="grid grid-cols-[1fr_110px] gap-2">
                          <Input
                            value={child.name}
                            onChange={(e) => handleChildDetailChange(index, 'name', e.target.value)}
                            placeholder={`Nome do filho ${index + 1}`}
                            className="h-11 rounded-lg"
                          />
                          <Input
                            type="number"
                            min="0"
                            max="30"
                            value={child.age}
                            onChange={(e) => handleChildDetailChange(index, 'age', e.target.value)}
                            placeholder="Idade"
                            className="h-11 rounded-lg"
                          />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider block">
                Vai levar acompanhante(s)?
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={newGuestHasCompanion ? 'outline' : 'default'}
                  className={`rounded-full ${newGuestHasCompanion ? '' : 'bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]'}`}
                  onClick={() => {
                    setNewGuestHasCompanion(false);
                    setNewGuestCompanionCount('1');
                    setNewGuestCompanionNames(['']);
                  }}
                >
                  Não
                </Button>
                <Button
                  type="button"
                  variant={newGuestHasCompanion ? 'default' : 'outline'}
                  className={`rounded-full ${newGuestHasCompanion ? 'bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]' : ''}`}
                  onClick={() => {
                    setNewGuestHasCompanion(true);
                    const normalizedCount = clampPeopleCount(newGuestCompanionCount || '1');
                    setNewGuestCompanionCount(String(normalizedCount));
                    resizeCompanionNames(normalizedCount);
                  }}
                >
                  Sim
                </Button>
              </div>
              {newGuestHasCompanion && (
                <div className="space-y-2">
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={newGuestCompanionCount}
                    onChange={(e) => handleCompanionCountChange(e.target.value)}
                    placeholder="Quantidade de acompanhantes"
                    className="h-11 rounded-lg"
                  />
                  <div className="grid gap-2">
                    {newGuestCompanionNames
                      .slice(0, clampPeopleCount(newGuestCompanionCount))
                      .map((name, index) => (
                        <Input
                          key={`companion-${index}`}
                          value={name}
                          onChange={(e) => handleCompanionNameChange(index, e.target.value)}
                          placeholder={`Nome do acompanhante ${index + 1}`}
                          className="h-11 rounded-lg"
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  resetAddGuestForm();
                }}
                className="rounded-full"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#E8B7B1] text-white rounded-full">
                {submitting ? 'Adicionando...' : 'Adicionar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={messageViewer.open} onOpenChange={handleMessageViewerChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              {messageViewer.title}
            </DialogTitle>
            {messageViewer.author && (
              <DialogDescription>
                Enviada por {messageViewer.author}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="rounded-2xl bg-[#F8F5F2] p-4 text-sm leading-relaxed text-[#333333] whitespace-pre-wrap break-words">
            {messageViewer.message}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              Importar Convidados
            </DialogTitle>
            <DialogDescription>
              Cole a lista de nomes ou importe de um arquivo Excel/CSV
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-12 rounded-lg border-dashed border-2"
              >
                <Upload className="w-5 h-5 mr-2" />
                Selecionar Arquivo (CSV, Excel ou TXT)
              </Button>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-[#333333]">ou cole aqui</span>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                Lista de Nomes (um por linha)
              </Label>
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Maria Silva&#10;João Santos&#10;Ana Oliveira"
                className="w-full h-48 p-3 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#E8B7B1]"
                data-testid="import-names-textarea"
              />
              <p className="text-xs text-[#333333] mt-1">
                {importText.split('\n').filter(n => n.trim()).length} nome(s) para importar
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowImportModal(false)} className="rounded-full">
                Cancelar
              </Button>
              <Button 
                onClick={handleImportGuests} 
                disabled={submitting || !importText.trim()} 
                className="bg-[#E8B7B1] text-white rounded-full"
              >
                {submitting ? 'Importando...' : 'Importar'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <SupportWidget />
    </div>
  );
}

